import { ReactNode } from 'react';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { ActionMeta, GroupBase, Props, SingleValue } from 'react-select';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Select from 'components/Select/Select';
import { SelectOptionT } from 'declarations/types/select';

type FormSelectPropsT<
  FormValuesT,
  Option extends SelectOptionT = SelectOptionT,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<Props<Option, IsMulti, Group>, 'options' | 'value' | 'onChange' | 'defaultValue'> & {
  name: Path<FormValuesT>;
  options: SelectOptionT[];
  defaultValue?: SelectOptionT['value'];
  placeholder?: string;
  label?: string | ReactNode;
  isRequired?: boolean;
  onValueChange?: ((
    newValue: SingleValue<SelectOptionT>,
    actionMeta: ActionMeta<SelectOptionT>
  ) => void) &
    ((option: SingleValue<string | number>) => void);
};

const FormSelect = <FormValuesT,>({
  name,
  onValueChange,
  ...restFormSelectProps
}: FormSelectPropsT<FormValuesT>) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { ref, ...restFieldProps } }) => (
          <Select {...restFieldProps} {...restFormSelectProps} onValueChange={onValueChange} />
        )}
      />
      {errors[name] && <ErrorMessage error={errors[name]?.message as Path<FormValuesT>} />}
    </>
  );
};

export default FormSelect;
